import React, { useEffect, useRef, useState } from 'react';
import Close from '../../../../../Assets/Modals/closeIcon.svg';
import { useDispatch } from 'react-redux';
import { closeModal } from '../../../../../Features/lensSlice';
import Prev from '../../../../../Assets/Modals/Prev.svg';
import Next from '../../../../../Assets/Modals/Next.svg';
import Play from '../../../../../Assets/Lens/Play.svg';
import Pause from '../../../../../Assets/Lens/Pause.svg';
import SoundFull from '../../../../../Assets/Lens/RadioIcon.svg';
import SoundMute from '../../../../../Assets/Lens/RadioMute.svg';
import { getCachedMediaUrl } from '../../../../../lib/utils/utils';

function PostMedia({ post }) {
    const dispatch = useDispatch();

    const [slide, setSlide] = useState(0);
    const videoRef = useRef(null);

    const [isPlaying, setIsPlaying] = useState(false);
    const [controlsVisible, setControlsVisible] = useState(false);
    const [isMuted, setIsMuted] = useState(false); // respect user's choice; don't force change

    const hasImages = Array.isArray(post?.imageUrls) && post.imageUrls.length > 0;
    const hasMultipleImages = Array.isArray(post?.imageUrls) && post.imageUrls.length > 1;
    const hasVideo = Boolean(post?.videoUrls);

    const playVideo = () => {
        const v = videoRef.current;
        if (!v) return;
        // Honor current user preference
        v.muted = isMuted;
        v.play().then(() => {
            setIsPlaying(true);
        }).catch((err) => {
            // If browser blocks autoplay with sound, you can decide to fallback:
            // For modal + explicit user click, this should rarely happen.
            console.error('Video play error:', err);
        });
    };

    const pauseVideo = () => {
        const v = videoRef.current;
        if (!v) return;
        v.pause();
        setIsPlaying(false);
    };

    const forwardSlide = () => {
        if (!hasMultipleImages) return;
        setSlide((prev) => (prev === post.imageUrls.length - 1 ? 0 : prev + 1));
    };

    const backwardSlide = () => {
        if (!hasMultipleImages) return;
        setSlide((prev) => (prev === 0 ? post.imageUrls.length - 1 : prev - 1));
    };

    const toggleAudio = () => {
        const v = videoRef.current;
        if (!v) return;
        const next = !v.muted;
        v.muted = next;
        setIsMuted(next);
        // Do NOT touch isPlaying here; only audio changes
    };

    const handleCloseModal = () => {
        dispatch(closeModal());
    };

    useEffect(() => {
        const handleEsc = (event) => {
            if (event.key === 'Escape' || event.keyCode === 27) {
                dispatch(closeModal());
            }
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [dispatch]);

    return (
        <div
            className="w-3/5 relative h-full bg-[#EBEBEB]"
            onMouseEnter={() => setControlsVisible(true)}
            onMouseLeave={() => setControlsVisible(false)}
        >
            <button
                onClick={handleCloseModal}
                className="absolute top-2 left-2 w-11 h-11 flex items-center justify-center"
            >
                <img src={Close} alt="Close" />
            </button>

            {hasImages && (
                <div className="w-full h-full flex items-center justify-center">
                    <div className="h-full w-full flex items-center justify-center">
                        {post.imageUrls.map((url, index) => (
                            <img
                                key={url}
                                className={`max-h-full max-w-full ${slide === index ? '' : 'hidden'}`}
                                src={getCachedMediaUrl(url)}
                                alt=""
                            />
                        ))}
                    </div>
                </div>
            )}

            {hasMultipleImages && (
                <>
                    <button
                        onClick={backwardSlide}
                        className="rounded-full bg-[#FFFFFF8A] hover:bg-[#FFFFFFBC] absolute top-1/2 left-3 -translate-y-1/2 w-11 h-11 flex items-center justify-center"
                    >
                        <img src={Prev} alt="Previous" />
                    </button>

                    <button
                        onClick={forwardSlide}
                        className="rounded-full bg-[#FFFFFF8A] hover:bg-[#FFFFFFBC] absolute top-1/2 right-3 -translate-y-1/2 w-11 h-11 flex items-center justify-center"
                    >
                        <img src={Next} alt="Next" />
                    </button>
                </>
            )}

            {hasVideo && (
                <div className="w-full h-full flex items-center justify-center">
                    <video
                        ref={videoRef}
                        className="relative max-w-full max-h-full"
                        src={post.videoUrls}
                        playsInline
                        preload="metadata"
                        onEnded={() => setIsPlaying(false)} 
                        onError={(e) => console.error('Video error:', e)}
                    />
                </div>
            )}

            {hasVideo && (
                <button
                    onClick={toggleAudio}
                    className={`${!controlsVisible && 'opacity-0'} select-none transition-all delay-100 ease-in-out absolute bottom-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-[#F5F5F5]`}
                >
                    <div className="w-[18px] h-[18px] flex items-center justify-center">
                        <img src={isMuted ? SoundMute : SoundFull} alt="Sound" />
                    </div>
                </button>
            )}

            {hasVideo && (
                <>
                    {isPlaying ? (
                        <button
                            onClick={pauseVideo}
                            className={`${!controlsVisible && 'opacity-0'} select-none transition-all delay-100 ease-in-out absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[50px] h-[50px] flex items-center justify-center`}
                        >
                            <img className="w-full h-full" src={Pause} alt="Pause" />
                        </button>
                    ) : (
                        <button
                            onClick={playVideo}
                            className="select-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[50px] h-[50px] flex items-center justify-center"
                        >
                            <img className="w-full h-full" src={Play} alt="Play" />
                        </button>
                    )}
                </>
            )}
        </div>
    );
}

export default PostMedia;
